import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Dialog, DialogTitle, DialogContent, IconButton, Typography,
  CircularProgress, Checkbox, FormControlLabel
} from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTR = '&copy; OpenStreetMap contributors'

const PLAN_COLORS = [
  '#2E7D32', // green — mixed load
  '#1565C0', // blue — combined #1
  '#E65100', // orange — split
  '#7B1FA2', // purple — combined #2
  '#00838F', // teal — combined #3
  '#AD1457', // pink
  '#F9A825', // amber
  '#4E342E'  // brown
]

function makeIcon (color, label, size = 24) {
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};color:#fff;font-size:${size > 24 ? 11 : 9}px;font-weight:700;
      display:flex;align-items:center;justify-content:center;
      border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3);
    ">${label}</div>`
  })
}

/**
 * DisposalRouteCompareMap — full-page modal showing all plan routes overlaid on one map.
 *
 * Props:
 *   open         {boolean}
 *   onClose      {function}
 *   url          {string}
 *   hmiCoords    {{ lat, lng }}
 *   jobCoords    {{ lat, lng }}
 *   plans        {Array<{ name, type, color, facilities: [{lat, lng, name}], cost, trips }>}
 *   formatMoney  {function}
 */
const DisposalRouteCompareMap = ({ open, onClose, url, hmiCoords, jobCoords, plans, formatMoney }) => {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const layerGroupsRef = useRef({})
  const [loading, setLoading] = useState(false)
  const [enabledPlans, setEnabledPlans] = useState({})

  // Initialize all plans as enabled
  useEffect(() => {
    if (open && plans?.length) {
      const init = {}
      plans.forEach((_, i) => { init[i] = true })
      setEnabledPlans(init)
    }
  }, [open, plans])

  useEffect(() => {
    if (!open || !mapRef.current || !hmiCoords || !jobCoords || !plans?.length) return

    // Small delay for dialog animation
    const timer = setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }

      const map = L.map(mapRef.current, { zoomControl: true })
      mapInstanceRef.current = map
      L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 18 }).addTo(map)

      // Add HMI and Jobsite markers (always visible)
      L.marker([hmiCoords.lat, hmiCoords.lng], { icon: makeIcon('#2E7D32', 'HMI', 30), zIndexOffset: 1000 })
        .bindTooltip('HMI Yard', { permanent: true, direction: 'top', offset: [0, -16] }).addTo(map)
      L.marker([jobCoords.lat, jobCoords.lng], { icon: makeIcon('#1565C0', 'JOB', 30), zIndexOffset: 1000 })
        .bindTooltip('Jobsite', { permanent: true, direction: 'top', offset: [0, -16] }).addTo(map)

      // Collect all points for bounds
      const allPts = [[hmiCoords.lat, hmiCoords.lng], [jobCoords.lat, jobCoords.lng]]
      plans.forEach(plan => {
        plan.facilities.forEach(f => allPts.push([f.lat, f.lng]))
      })
      map.fitBounds(allPts, { padding: [50, 50] })

      // Fetch route geometry for each plan
      setLoading(true)
      const promises = plans.map((plan, idx) => {
        const color = plan.color || PLAN_COLORS[idx % PLAN_COLORS.length]
        const waypoints = [hmiCoords, jobCoords, ...plan.facilities, hmiCoords]

        return axios.post(`${url}/api/aggregate-dashboard/fulfillment-calc/disposal/route-geometry`, { waypoints }, { timeout: 15000 })
          .then(res => {
            const geo = res.data?.geometry || []
            const layerGroup = L.layerGroup()

            if (geo.length >= 2) {
              L.polyline(geo, { color, weight: 4, opacity: 0.75 }).addTo(layerGroup)
            } else {
              // Straight-line fallback
              const pts = waypoints.map(w => [w.lat, w.lng])
              L.polyline(pts, { color, weight: 3, opacity: 0.5, dashArray: '8,6' }).addTo(layerGroup)
            }

            // Facility markers for this plan
            plan.facilities.forEach((f, fi) => {
              L.marker([f.lat, f.lng], { icon: makeIcon(color, `F${fi + 1}`) })
                .bindTooltip(f.name || 'Facility', { permanent: false }).addTo(layerGroup)
            })

            layerGroup.addTo(map)
            layerGroupsRef.current[idx] = layerGroup
          })
          .catch(() => {
            // Straight-line fallback
            const layerGroup = L.layerGroup()
            const pts = waypoints.map(w => [w.lat, w.lng])
            L.polyline(pts, { color, weight: 3, opacity: 0.5, dashArray: '8,6' }).addTo(layerGroup)
            plan.facilities.forEach((f, fi) => {
              L.marker([f.lat, f.lng], { icon: makeIcon(color, `F${fi + 1}`) })
                .bindTooltip(f.name || 'Facility', { permanent: false }).addTo(layerGroup)
            })
            layerGroup.addTo(map)
            layerGroupsRef.current[idx] = layerGroup
          })
      })

      Promise.all(promises).finally(() => setLoading(false))
    }, 200)

    return () => {
      clearTimeout(timer)
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
      layerGroupsRef.current = {}
    }
  }, [open, hmiCoords, jobCoords, plans, url])

  // Toggle plan visibility
  const togglePlan = (idx) => {
    setEnabledPlans(prev => {
      const next = { ...prev, [idx]: !prev[idx] }
      const layer = layerGroupsRef.current[idx]
      const map = mapInstanceRef.current
      if (layer && map) {
        if (next[idx]) {
          layer.addTo(map)
        } else {
          map.removeLayer(layer)
        }
      }
      return next
    })
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ style: { background: '#f5f0e5' } }}>
      <DialogTitle disableTypography style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 20px', background: 'var(--surface, #fbf8f1)',
        borderBottom: '1px solid var(--line, #e0d8c2)'
      }}>
        <Typography variant="h6" style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink, #1c2419)' }}>
          Route Comparison
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: 0, display: 'flex', height: '100%' }}>
        {/* Side panel: plan legend + toggles */}
        <div style={{
          width: 280, minWidth: 280, background: 'var(--surface, #fbf8f1)',
          borderRight: '1px solid var(--line, #e0d8c2)', padding: '16px 12px',
          overflowY: 'auto'
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
            color: 'var(--ink-3, #6f7a6b)', marginBottom: 12, fontFamily: 'var(--font-mono)'
          }}>Plans</div>

          {(plans || []).map((plan, idx) => {
            const color = plan.color || PLAN_COLORS[idx % PLAN_COLORS.length]
            return (
              <div key={idx} style={{
                marginBottom: 8, padding: '8px 10px', borderRadius: 8,
                background: enabledPlans[idx] ? `${color}11` : 'transparent',
                border: `1px solid ${enabledPlans[idx] ? `${color}33` : 'var(--line, #e0d8c2)'}`,
                transition: 'all .2s'
              }}>
                <FormControlLabel
                  control={<Checkbox checked={!!enabledPlans[idx]} onChange={() => togglePlan(idx)}
                    size="small" style={{ color, padding: 2, marginRight: 6 }} />}
                  label={
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink, #1c2419)' }}>
                        <span style={{
                          display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                          background: color, marginRight: 6, verticalAlign: 'middle'
                        }} />
                        {plan.name}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)', marginTop: 2 }}>
                        {formatMoney(plan.cost)} &middot; {plan.trips} trip{plan.trips !== 1 ? 's' : ''}
                      </div>
                      {plan.facilities.map((f, fi) => (
                        <div key={fi} style={{ fontSize: 9, color: 'var(--ink-4, #97a094)', marginTop: 1, paddingLeft: 16 }}>
                          F{fi + 1}: {f.name}
                        </div>
                      ))}
                    </div>
                  }
                  style={{ alignItems: 'flex-start', margin: 0 }}
                />
              </div>
            )
          })}

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, justifyContent: 'center' }}>
              <CircularProgress size={16} />
              <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>Loading routes...</span>
            </div>
          )}
        </div>

        {/* Map */}
        <div style={{ flex: 1, position: 'relative' }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { PLAN_COLORS }
export default DisposalRouteCompareMap
